import { authStoreStateApiRepository } from '@/infra/repositories/authStoreState.repository';
import type { AuthStateManagerRepository } from '@/shared/contracts/authStateManager.repository';
import type { AuthStoreStateRepository } from '@/shared/contracts/authStoreState.repository';
import type { TokenValidatorRepository } from '@/shared/contracts/tokenValidator.repository';
import type { AuthUserEntityStore } from '@/shared/models';
import { jwtTokenValidatorService } from './jwtTokenValidator.service';

/**
 * Servicio de gestión de estado de autenticación con validación de tokens
 * Cumple con SRP: solo se encarga de validar y gestionar estado de sesión
 * Usa dependency injection para acceso al auth store
 */
class AuthStateManagerService implements AuthStateManagerRepository {
  constructor(
    private tokenValidator: TokenValidatorRepository,
    private authStoreState: AuthStoreStateRepository,
  ) {}

  /**
   * Valida que el estado de autenticación sea completo y el token válido
   * @param authState - Estado de autenticación a validar
   */
  validateAuthState(authState: AuthUserEntityStore): boolean {
    return !!(
      authState.token &&
      this.tokenValidator.validateToken(authState.token) &&
      authState.role &&
      authState.email &&
      authState.name
    );
  }

  /**
   * Determina si el estado debe limpiarse por token inválido
   * @param authState - Estado de autenticación a verificar
   */
  shouldClearAuthState(authState: AuthUserEntityStore): boolean {
    return !!(
      authState.token && !this.tokenValidator.validateToken(authState.token)
    );
  }

  /**
   * Sanitiza y normaliza los datos del estado de autenticación
   * @param authState - Estado a sanitizar
   */
  sanitizeAuthState(authState: AuthUserEntityStore): AuthUserEntityStore {
    return {
      id: authState.id,
      name: authState.name?.trim() || null,
      email: authState.email?.toLowerCase().trim() || null,
      run: authState.run?.trim() || null,
      role: authState.role?.trim() || null,
      token: authState.token?.trim() || null,
      es_admin: authState.es_admin || null,
      ultimo_acceso: authState.ultimo_acceso?.trim() || null,
      cod_tipo_perfil: authState.cod_tipo_perfil || null,
      desc_tipo_perfil: authState.desc_tipo_perfil?.trim() || null,
    };
  }

  /**
   * Verifica si el usuario está autenticado con sesión válida
   */
  isUserAuthenticated(): boolean {
    const authState = this.authStoreState.getCurrentAuthState();
    return this.validateAuthState(authState);
  }

  /**
   * Verifica si existe un token de autenticación en el estado
   */
  hasToken(): boolean {
    const authState = this.authStoreState.getCurrentAuthState();
    return !!(authState.token && authState.token.trim().length > 0);
  }
}

export const authStateManagerService = new AuthStateManagerService(
  jwtTokenValidatorService,
  authStoreStateApiRepository,
);
